// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides functionality to update organization details in the remote API.
 */
import * as z from 'zod';
import type { SerialPatternKind } from '@/account/api/types';
import { sparseOrganizationSchema } from '@/account/api/types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { OrgDefaultCurrencyCode } from '@/constants/currency';

/**
 * Schema for validating and transforming the API response when updating an organization.
 * Restructures the response to expose the organization directly.
 */
const updateOrganizationResponseSchema = z
  .object({
    data: sparseOrganizationSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, organization: data }));

/** Type representing the validated response from updating an organization */
type UpdateOrganizationResponse = z.infer<
  typeof updateOrganizationResponseSchema
>;

/**
 * Interface defining the parameters that can be updated for an organization.
 * All fields are optional to allow partial updates.
 */
export interface UpdateOrganizationParameters {
  /** The new name for the organization */
  name?: string;
  /** The new timezone for the organization (IANA timezone identifier) */
  timezone?: string;
  /** The new asset marker for the organization */
  assetMarker?: string;
  /** The default bill-from address copied onto new invoices */
  billingAddress?: string | null;
  /** The fallback payment term used for new invoices */
  defaultPaymentTerm?: string | null;
  /** The default invoice memo copied onto new invoices */
  defaultInvoiceMemo?: string | null;
  /** The default payment instructions copied onto new invoices */
  defaultPaymentInstructions?: string | null;
  /** The prefix used for future auto-generated invoice numbers */
  invoiceNumberPrefix?: string | null;
  /**
   * Weighted average cost of capital used by the billing-lifecycle carrying
   * cost calculation. Sent as a decimal string in [0.01, 1.00] (1% .. 100%).
   * Cannot be cleared — every organization carries an effective WACC.
   */
  weightedAverageCostOfCapital?: string;
  /**
   * Invoicing default currency. Cannot be cleared — every organization
   * carries an effective currency.
   */
  defaultCurrency?: OrgDefaultCurrencyCode;
  /**
   * The organization's default origin location id, used as a fallback when new
   * flows need an origin. Pass null to clear the default.
   */
  defaultOriginLocationId?: string | null;
  /**
   * The organization's default return location id, used as a fallback when new
   * return orders need a destination. Pass null to clear the default.
   */
  defaultReturnLocationId?: string | null;
  /**
   * The organization's default receive location id, used as a fallback when new
   * purchase orders need a destination. Pass null to clear the default.
   */
  defaultReceiveLocationId?: string | null;
  /**
   * The organization's full set of serial patterns, which replaces whatever is
   * stored. An empty list clears them, which accepts every reported serial.
   */
  serialPatterns?: SerialPatternInput[];
}

/**
 * One serial pattern as the update endpoint takes it. `length` and `prefix` are
 * null for the kinds that do not use them.
 */
export interface SerialPatternInput {
  kind: SerialPatternKind;
  length: number | null;
  prefix: string | null;
}

/**
 * Updates an organization record in the remote API.
 *
 * @param organizationKey - The key of the organization to update.
 * @param parameters - The parameters to update, which can include name and timezone.
 * @returns A promise that resolves to the validated response containing the updated organization.
 * @throws Will throw if the API request fails or if the response fails validation.
 */
export async function updateOrganization(
  organizationKey: string,
  parameters: UpdateOrganizationParameters
): Promise<UpdateOrganizationResponse> {
  const url = `/v1/organization/${organizationKey}`;
  const response = await api.patch(url, parameters);

  return parseResponse(updateOrganizationResponseSchema, response.data, {
    schemaName: 'UpdateOrganization',
    endpoint: url,
  });
}
